/**
 * PlanIntake — the one-time AI plan goal-setting questionnaire.
 *
 * This is the shape the backend accepts on `POST /programme/one-time` and the UI
 * renders (see `docs/architecture/app-backend-contract.md` §5). Every field name
 * matches that contract one-for-one; `track` and the migration's prescription
 * vocabulary are additive and do not rename anything.
 *
 * Lift2Fit is gym-based and indoor. The intake therefore asks what a customer can
 * train *in a gym* — never for a track, field or outdoor space.
 *
 * Determinism: the schema is pure. `defaultIntake()` returns a new object every
 * call but always with identical values, so the builder stays reproducible.
 */
import { z } from 'zod';
import { ExperienceSchema } from '../types/coach.js';

/** The engine's experience levels, re-derived here so the plan module is self-contained. */
export type Experience = z.infer<typeof ExperienceSchema>;

/**
 * The specialist-coach taxonomy. One intake, six specialists — the track decides
 * the split, the training variables, the exercise pool and the metrics the app
 * tracks. `general` is the safe default when a customer does not pick.
 */
export const TrainingTrackSchema = z.enum([
  'strength',
  'hypertrophy',
  'conditioning',
  'performance',
  'wellness',
  'general',
]);
export type TrainingTrack = z.infer<typeof TrainingTrackSchema>;
export const TRAINING_TRACKS: ReadonlyArray<TrainingTrack> = TrainingTrackSchema.options;

/** The contract's original goal field is retained; the track is authoritative. */
export const PlanGoalSchema = z.enum([
  'strength',
  'hypertrophy',
  'fat_loss',
  'endurance',
  'general',
]);
export type PlanGoal = z.infer<typeof PlanGoalSchema>;

/** Days per week the customer will actually train. */
export const DaysPerWeekSchema = z.union([
  z.literal(2),
  z.literal(3),
  z.literal(4),
  z.literal(5),
  z.literal(6),
]);
export type DaysPerWeek = z.infer<typeof DaysPerWeekSchema>;
export const DAYS_PER_WEEK_VALUES: ReadonlyArray<DaysPerWeek> = [2, 3, 4, 5, 6];

/** Session length in minutes; the builder caps work to fit the shortest option. */
export const SessionMinutesSchema = z.union([
  z.literal(30),
  z.literal(45),
  z.literal(60),
  z.literal(75),
  z.literal(90),
]);
export type SessionMinutes = z.infer<typeof SessionMinutesSchema>;
export const SESSION_MINUTES_VALUES: ReadonlyArray<SessionMinutes> = [30, 45, 60, 75, 90];

export const SexSchema = z.enum(['female', 'male', 'unspecified']);
export type Sex = z.infer<typeof SexSchema>;

/** Equipment tokens the seed library and the UI both use. `bodyweight` is always available. */
export const EquipmentSchema = z.enum([
  'barbell',
  'dumbbell',
  'machine',
  'cable',
  'bodyweight',
  'kettlebell',
  'band',
]);
export type Equipment = z.infer<typeof EquipmentSchema>;
export const EQUIPMENT_VALUES: ReadonlyArray<Equipment> = EquipmentSchema.options;

/**
 * Canonical injury flags. These are exactly the flags the safety rails know how to
 * route (`INJURY_MOVEMENT_MAP`), so an intake can never carry an injury we would
 * silently ignore. `test/plan-intake.test.ts` asserts the two stay in lock-step.
 */
export const INJURY_FLAG_VALUES = [
  'left_shoulder',
  'right_shoulder',
  'lower_back',
  'left_knee',
  'right_knee',
  'left_elbow',
  'right_elbow',
  'left_wrist',
  'right_wrist',
] as const;
export const InjuryFlagSchema = z.enum(INJURY_FLAG_VALUES);
export type InjuryFlag = z.infer<typeof InjuryFlagSchema>;

/**
 * Bounds are deliberately wide enough for a real adult population and narrow
 * enough that a typo (lbs entered as kg, a birth year typed as an age) is caught
 * at the edge rather than producing a nonsense programme.
 */
export const MIN_HEIGHT_CM = 120;
export const MAX_HEIGHT_CM = 230;
export const MIN_BODYWEIGHT_KG = 30;
export const MAX_BODYWEIGHT_KG = 300;
export const MIN_AGE = 14;
export const MAX_AGE = 90;
export const MAX_NOTES_LENGTH = 2000;

export const PlanIntakeSchema = z
  .object({
    /** Specialist coach to build. Defaults to all-round fitness. */
    track: TrainingTrackSchema.default('general'),
    /** Contract goal field, kept for compatibility; `track` wins when they differ. */
    goal: PlanGoalSchema,
    experience: ExperienceSchema,
    daysPerWeek: DaysPerWeekSchema,
    sessionMinutes: SessionMinutesSchema,
    heightCm: z.number().finite().min(MIN_HEIGHT_CM).max(MAX_HEIGHT_CM),
    bodyweightKg: z.number().finite().min(MIN_BODYWEIGHT_KG).max(MAX_BODYWEIGHT_KG),
    age: z.number().int().min(MIN_AGE).max(MAX_AGE),
    sex: SexSchema,
    /** Empty is valid: a bodyweight-only customer needs no equipment. */
    equipment: z.array(EquipmentSchema).max(EQUIPMENT_VALUES.length).default([]),
    injuryFlags: z.array(InjuryFlagSchema).max(INJURY_FLAG_VALUES.length).default([]),
    notes: z.string().trim().max(MAX_NOTES_LENGTH).optional(),
  })
  // Unknown keys are rejected: the backend and UI code against this contract, and a
  // silent typo would otherwise produce a plan from a field we never read.
  .strict();
export type PlanIntake = z.infer<typeof PlanIntakeSchema>;

/** Parse a raw questionnaire payload (e.g. straight from the API body). */
export function parsePlanIntake(raw: unknown): PlanIntake {
  return PlanIntakeSchema.parse(raw);
}

/** A sensible, safe starting questionnaire: bodyweight-only, 3 days, 60 minutes. */
export const DEFAULT_INTAKE: PlanIntake = PlanIntakeSchema.parse({
  track: 'general',
  goal: 'general',
  experience: 'beginner',
  daysPerWeek: 3,
  sessionMinutes: 60,
  heightCm: 175,
  bodyweightKg: 75,
  age: 30,
  sex: 'unspecified',
  equipment: ['bodyweight'],
  injuryFlags: [],
});

/** `defaultIntake({ track: 'wellness' })` — clone the defaults and override. */
export function defaultIntake(overrides: Partial<PlanIntake> = {}): PlanIntake {
  return PlanIntakeSchema.parse({ ...DEFAULT_INTAKE, ...overrides });
}
